import { router } from '@inertiajs/react';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/X';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { FONT_BODY, FONT_DISPLAY, FONT_MONO, TEAL } from './LandingKit';
import SitemarkIcon from './SitemarkIcon';

// Footer links inherit a teal hover so they match the rest of the landing.
const footerLinkSx = (theme) => ({
  fontFamily: FONT_BODY,
  fontSize: '0.875rem',
  color: '#33514c',
  textDecorationColor: 'transparent',
  transition: 'color 0.15s ease',
  '&:hover': { color: TEAL },
  ...theme.applyStyles('dark', { color: '#9fc4bd' }),
});

const columnLabelSx = (theme) => ({
  fontFamily: FONT_MONO,
  fontSize: 11,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: '#0a3f39',
  mb: 0.5,
  ...theme.applyStyles('dark', { color: '#7df0e2' }),
});

function Wordmark() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <SitemarkIcon />
      <Typography
        component="span"
        sx={{
          fontFamily: FONT_DISPLAY,
          fontWeight: 800,
          fontSize: 18,
          letterSpacing: '-0.01em',
          lineHeight: 1,
          color: 'text.primary',
        }}
      >
        Drive<Box component="span" sx={{ color: TEAL }}>Sync</Box>
      </Typography>
    </Box>
  );
}

function Copyright() {
  return (
    <Typography
      sx={(theme) => ({
        fontFamily: FONT_BODY,
        fontSize: '0.8rem',
        color: '#33514c',
        mt: 1,
        ...theme.applyStyles('dark', { color: '#9fc4bd' }),
      })}
    >
      {'Copyright © '}
      <Link href="https://drivesync.com.br/" sx={{ color: TEAL, textDecorationColor: 'transparent' }}>
        DriveSync
      </Link>
      &nbsp;
      {new Date().getFullYear()}
    </Typography>
  );
}

export default function Footer() {
  const handlePrivacyClick = (e) => {
    e.preventDefault();
    router.visit('/privacy');
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    router.visit('/privacy');
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    router.visit('/leads/new');
  };

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        borderTop: '1px solid',
        borderColor: `${TEAL}22`,
        backgroundColor: '#e7eeec',
        ...theme.applyStyles('dark', {
          borderColor: 'rgba(255,255,255,0.08)',
          backgroundColor: '#04070a',
        }),
      })}
    >
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(60% 80% at 0% 0%, ${TEAL}14, transparent 70%)`,
          ...theme.applyStyles('dark', {
            background: `radial-gradient(60% 80% at 0% 0%, ${TEAL}1f, transparent 70%)`,
          }),
        })}
      />
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 8 },
          py: { xs: 8, sm: 10 },
          textAlign: { sm: 'center', md: 'left' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              minWidth: { xs: '100%', sm: '60%' },
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: '60%' } }}>
              <Wordmark />
              <Typography
                sx={(theme) => ({
                  fontFamily: FONT_BODY,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  mt: 2,
                  color: '#071513',
                  ...theme.applyStyles('dark', { color: '#f2fbf9' }),
                })}
              >
                Receba novidades e promoções
              </Typography>
              <Typography
                sx={(theme) => ({
                  fontFamily: FONT_BODY,
                  fontSize: '0.85rem',
                  color: '#33514c',
                  mb: 2,
                  ...theme.applyStyles('dark', { color: '#9fc4bd' }),
                })}
              >
                Não enviamos spam.
              </Typography>
              <InputLabel
                htmlFor="email-newsletter"
                sx={(theme) => ({
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#0a3f39',
                  mb: 0.75,
                  ...theme.applyStyles('dark', { color: '#7df0e2' }),
                })}
              >
                Email
              </InputLabel>
              <Stack direction="row" spacing={1} useFlexGap>
                <TextField
                  id="email-newsletter"
                  hiddenLabel
                  size="small"
                  variant="outlined"
                  fullWidth
                  aria-label="Digite seu email"
                  placeholder="Digite seu email"
                  slotProps={{
                    htmlInput: {
                      autoComplete: 'off',
                      'aria-label': 'Digite seu email',
                    },
                  }}
                  sx={{
                    width: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      fontFamily: FONT_BODY,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL },
                    },
                  }}
                />
                <Button
                  size="small"
                  disableElevation
                  sx={{
                    flexShrink: 0,
                    px: 2.5,
                    borderRadius: 999,
                    fontFamily: FONT_BODY,
                    fontWeight: 700,
                    textTransform: 'none',
                    color: '#04110f',
                    background: `linear-gradient(180deg, ${TEAL}, #00a594)`,
                    '&:hover': {
                      background: `linear-gradient(180deg, ${TEAL}, #00a594)`,
                      boxShadow: `0 8px 20px ${TEAL}55`,
                    },
                  }}
                >
                  Inscrever-se
                </Button>
              </Stack>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography component="span" sx={columnLabelSx}>
              Produto
            </Typography>
            <Link sx={footerLinkSx} href="#features">
              Funcionalidades
            </Link>
            <Link sx={footerLinkSx} href="#how">
              Como funciona
            </Link>
            <Link sx={footerLinkSx} href="#highlights">
              Destaques
            </Link>
            <Link sx={footerLinkSx} href="#pricing">
              Preços
            </Link>
            <Link sx={footerLinkSx} href="#faq">
              Perguntas Frequentes
            </Link>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography component="span" sx={columnLabelSx}>
              Empresa
            </Typography>
            <Link sx={footerLinkSx} href="#">
              Sobre nós
            </Link>
            <Link sx={footerLinkSx} href="#">
              Carreiras
            </Link>
            <Link sx={footerLinkSx} href="#">
              Imprensa
            </Link>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography component="span" sx={columnLabelSx}>
              Legal
            </Typography>
            <Link
              sx={{ ...footerLinkSx, cursor: 'pointer' }}
              href="/privacy"
              onClick={handleTermsClick}
            >
              Termos de uso
            </Link>
            <Link
              sx={{ ...footerLinkSx, cursor: 'pointer' }}
              href="/privacy"
              onClick={handlePrivacyClick}
            >
              Política de privacidade
            </Link>
            <Link
              sx={{ ...footerLinkSx, cursor: 'pointer' }}
              href="/leads/new"
              onClick={handleContactClick}
            >
              Contato
            </Link>
          </Box>
        </Box>
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: { xs: 4, sm: 8 },
            width: '100%',
            borderTop: '1px solid',
            borderColor: `${TEAL}22`,
            ...theme.applyStyles('dark', { borderColor: 'rgba(255,255,255,0.08)' }),
          })}
        >
          <div>
            <Link
              sx={{ ...footerLinkSx, cursor: 'pointer' }}
              href="/privacy"
              onClick={handlePrivacyClick}
            >
              Política de privacidade
            </Link>
            <Typography component="span" sx={{ display: 'inline', mx: 0.5, opacity: 0.5 }}>
              &nbsp;•&nbsp;
            </Typography>
            <Link
              sx={{ ...footerLinkSx, cursor: 'pointer' }}
              href="/terms"
              onClick={handleTermsClick}
            >
              Termos de uso
            </Link>
            <Copyright />
          </div>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ justifyContent: 'left' }}
          >
            <IconButton
              size="small"
              href="#"
              aria-label="X"
              sx={(theme) => ({
                alignSelf: 'center',
                color: '#33514c',
                '&:hover': { color: TEAL },
                ...theme.applyStyles('dark', { color: '#9fc4bd' }),
              })}
            >
              <TwitterIcon />
            </IconButton>
            <IconButton
              size="small"
              href="#"
              aria-label="LinkedIn"
              sx={(theme) => ({
                alignSelf: 'center',
                color: '#33514c',
                '&:hover': { color: TEAL },
                ...theme.applyStyles('dark', { color: '#9fc4bd' }),
              })}
            >
              <LinkedInIcon />
            </IconButton>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
